import React, {useEffect, useRef} from "react";
import styled, {css} from "styled-components";
import {Observer} from "mobx-react";
import type {XyiconFeature} from "../../../../generated/api/base";
import {typesFeatures} from "../../../../data/state/AppStateConstants";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {useAppStore} from "../../../../StateManager";
import {TabChildV5} from "../../abstract/table/TabChildV5";
import {TabViewV5} from "../../widgets/tab/TabViewV5";
import {SettingsModuleNav, SettingsModuleSubNav} from "../../../../Enums";
import {TypeSettingsV5} from "./type/TypeSettingsV5";
import {FieldSettingsV5} from "./field/FieldSettingsV5";
import {LayoutSettingsV5} from "./layout/LayoutSettingsV5";

interface IModuleSettingsProps {
	readonly feature: XyiconFeature;
}

export const ModuleSettingsV5 = (props: IModuleSettingsProps) => {
	const {feature} = props;
	const appState = useAppStore((state) => state.appState);
	const modulesCreatingType = useAppStore((state) => state.modulesCreatingType);
	const setModulesCreatingType = useAppStore((state) => state.setModulesCreatingType);
	const {
		app: {navigation},
		layoutSettings,
	} = appState;
	const createButtonRef = useRef<HTMLDivElement>(null);

	const onChangeSettingsTab = (id: string) => {
		const parts = StringUtils.decomposeParts(location.hash).map((part) => part.value);
		parts[5] = id;
		const hash = parts.join("/");

		navigation.go(hash);
	};

	useEffect(() => {
		const onHashChange = () => {
			setModulesCreatingType("");
		};
		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
	}, [setModulesCreatingType]);

	return (
		// key makes sure that when the user switches to another feature, new component instance will be recreated
		// with a fresh state, so eg. when the user is creating a type, and switches to another feature,
		// the CreateTypePanel will not be visible anymore.
		<Observer>
			{() => {
				const typesFeature = typesFeatures[feature as XyiconFeature.Xyicon] || feature;
				let activeTab: SettingsModuleSubNav = location.hash.split("/").pop() as SettingsModuleSubNav;

				if (![SettingsModuleSubNav.TYPES, SettingsModuleSubNav.FIELDS, SettingsModuleSubNav.LAYOUTS].includes(activeTab)) {
					activeTab = SettingsModuleSubNav.TYPES;
					const hashArr = location.hash.split("/");
					const lastBit = hashArr.pop() as SettingsModuleNav;
					if (Object.values(SettingsModuleNav).includes(lastBit)) {
						hashArr.push(lastBit); // re-add it, because it's needed in this case
					}
					hashArr.push(activeTab);
					navigation.go(hashArr.join("/"), true);
				}

				return (
					<ModuleSettingsContainerStyled
						key={feature}
						$isCreatingFields={modulesCreatingType === SettingsModuleSubNav.FIELDS}
					>
						<TabViewV5
							className="settingsTab"
							selectedTabId={activeTab}
							onChangeSelectedTabId={onChangeSettingsTab}
							createButtonRef={createButtonRef}
							isSettings={true}
						>
							<TabChildV5
								id={SettingsModuleSubNav.TYPES}
								label="Types"
								title="Types"
							>
								<TypeSettingsV5
									feature={feature}
									typesFeature={typesFeature}
									createButtonParentRef={createButtonRef}
								/>
							</TabChildV5>
							<TabChildV5
								id={SettingsModuleSubNav.FIELDS}
								label="Fields"
								title="Fields"
							>
								<FieldSettingsV5
									feature={feature}
									typesFeature={typesFeature}
								/>
							</TabChildV5>
							<TabChildV5
								id={SettingsModuleSubNav.LAYOUTS}
								label="Layouts"
								title="Layouts"
							>
								<LayoutSettingsV5
									ref={layoutSettings as React.Ref<LayoutSettingsV5>}
									feature={feature}
								/>
							</TabChildV5>
						</TabViewV5>
					</ModuleSettingsContainerStyled>
				);
			}}
		</Observer>
	);
};

const ModuleSettingsContainerStyled = styled.div<{$isCreatingFields: boolean}>`
	width: 100%;
	${(props) =>
		props.$isCreatingFields &&
		css`
			&::before {
				content: "";
				position: fixed;
				right: 0;
				bottom: 0;
				width: calc(100%);
				height: calc(100%);
				background-color: #00000045;
				z-index: 13;
			}
		`}
`;
